import { inject, isDevMode, WritableSignal } from "@angular/core";
import {
  DevtoolService,
  REDUX_DEVTOOL,
  ReduxTrack,
} from "../utils/devtool.service";
import { Subject } from "rxjs";
import { StoreService } from "./store.service";
import { MethodLoading } from "../store-types.model";

/**
 * Service class for managing entities through the store and API.
 *
 * @template T - The type of entities managed by the service.
 */
export class StoreFacadeService<
  TState,
  TStore extends StoreService<TState> = StoreService<TState>,
> {
  public store: TStore;
  public updater$ = new Subject<{
    name: string;
    type: string;
    canceler$: Subject<void>;
  }>();

  devtool = inject(REDUX_DEVTOOL);

  public static newStoreFacadeService<TState>(initialState: TState) {
    return new StoreFacadeService<TState>(
      new StoreService<TState>(initialState),
    );
  }

  constructor(store: TStore) {
    this.store = store;
    if (isDevMode() && this.devtool) {
      DevtoolService.addFacade(this);
    }
  }

  getState(): {
    [K in keyof AugmentedState<this, TState>]: WritableSignal<
      AugmentedState<this, TState>[K]
    >;
  } {
    return this.store.getWritableState() as any;
  }

  @ReduxTrack
  public async patchState(statePart: Partial<TState> | Object) {
    this.store.patchState(statePart);
  }
}

type AugmentedState<This, S> = Omit<MethodLoading<This>, keyof S> & S;
